import type * as monacoEditor from "monaco-editor/esm/vs/editor/editor.api";

import type { Monaco } from "@components/MonacoHtmlEditor/config/monacoLoader";

import { insertTranslationTagClick, validateTranslationTagSelection } from ".";

export const setupMonacoCommands = ({
  editor,
  monaco,
  isSearchEnabled = true,
  shouldEnableInsertTranslationTagShortcut = false,
  onInsertTranslationTagShortcut,
}: {
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  monaco: Monaco;
  isSearchEnabled?: boolean;
  shouldEnableInsertTranslationTagShortcut?: boolean;
  onInsertTranslationTagShortcut?: () => void;
}) => {
  if (editor) {
    // Override commands to disallow formatting html (to avoid formatting issue with liquid strings)
    editor.addCommand(monaco.KeyCode.F1, () => {});
    editor.addCommand(monaco.KeyMod.Shift | monaco.KeyMod.Alt | monaco.KeyCode.KeyF, () => {});
    editor.addCommand(
      monaco.KeyMod.chord(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyK, monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyF),
      () => {}
    );
    if (!isSearchEnabled) {
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyF, () => {});
    } else {
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyF, () => {
        // Ensure editor is focused first
        editor.focus();
        // Explicitly bind Ctrl+F to the built-in search action
        editor.trigger("setupMonacoCommands:find", "actions.find", null);
      });
    }
    // Cmd + Alt + L (mac) / Ctrl + Alt + L (windows/linux) to insert translation tags.
    //
    // NOTE: When multiple Monaco editors exist on a page, `editor.addCommand` keybindings are registered
    // in Monaco's shared keybinding service, and the last registration can win. Using `onKeyDown` binds
    // the shortcut to the specific editor DOM instance, ensuring the focused editor handles it.
    editor.onKeyDown((e) => {
      if (!shouldEnableInsertTranslationTagShortcut) {
        return;
      }

      const isCtrlCmd = e.ctrlKey || e.metaKey;
      const isShortcut = isCtrlCmd && e.altKey && e.keyCode === monaco.KeyCode.KeyL;

      if (!isShortcut) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const selection = editor.getSelection();

      if (!selection || selection.isEmpty()) {
        return;
      }

      if (!validateTranslationTagSelection(selection, editor)) {
        return;
      }

      insertTranslationTagClick(editor);
      onInsertTranslationTagShortcut?.();
    });
  }
};
